var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@folio/stripes/components';
import { ProposalType } from '../../types/api';
import ProposalReportCard from './ProposalReportCard';
import css from './ProposalReportSheet.css';
/**
 * CZNA-553 — the sheet ("sjetina") itself: proposals awaiting approval in a period,
 * laid out to be printed and carried to the meeting.
 *
 * <p>Split from the route on purpose. Everything that decides what the paper SAYS is
 * here and takes its data as props, so it can be asserted without a network stub or a
 * date picker; the route owns the period controls, the query and the print trigger.
 *
 * <p>CZNA-1100 — one card per proposal instead of one table row: the meeting needs the
 * proposal's own MARC content next to its metadata, not just a heading. Still a plain
 * DOM, not `MultiColumnList`: MCL virtualizes, so it renders the rows that fit the
 * viewport and no more. On screen that is a feature; on paper it means the printout ends
 * wherever the window happened to end. A print artifact has to be complete in the DOM
 * before the browser paginates it.
 *
 * <p>CZNA-1100 follow-up — up to 1000 cards on screen at once was slow to render, so each
 * section is paginated on screen (`PAGE_SIZE`). That would break the same print-completeness
 * guarantee above, so the route sets `renderAllForPrint` true for the moment `window.print()`
 * runs (see `ProposalsReport.tsx`'s `afterprint` handling) and this component bypasses its
 * own slicing while that prop is true — the paper always gets every row, regardless of which
 * on-screen page was showing.
 */
var PAGE_SIZE = 50;
var ProposalReportSheet = function (_a) {
    var report = _a.report, getOrganizationName = _a.getOrganizationName, getUserName = _a.getUserName, _b = _a.renderAllForPrint, renderAllForPrint = _b === void 0 ? false : _b;
    var intl = useIntl();
    var _c = useState({ new: 0, updates: 0 }), pages = _c[0], setPages = _c[1];
    // A new report (different period/filter) must not keep showing page 3 of the old one.
    useEffect(function () {
        setPages({ new: 0, updates: 0 });
    }, [report]);
    var formatDate = function (value) {
        return value ? intl.formatDate(value, { day: 'numeric', month: 'numeric', year: 'numeric' }) : '—';
    };
    var formatDateTime = function (value) {
        if (!value)
            return '—';
        var date = intl.formatDate(value, { day: 'numeric', month: 'numeric', year: 'numeric' });
        var time = intl.formatTime(value, { hour: '2-digit', minute: '2-digit' });
        return "".concat(date, " ").concat(time);
    };
    var newAuthorities = report.items.filter(function (item) { return item.proposalType === ProposalType.CREATE_NEW; });
    var amendments = report.items.filter(function (item) { return item.proposalType === ProposalType.UPDATE_EXISTING; });
    var renderSection = function (titleId, items, total, withTarget) {
        var sectionKey = withTarget ? 'updates' : 'new';
        var pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
        var page = Math.min(pages[sectionKey], pageCount - 1);
        var displayedItems = renderAllForPrint
            ? items
            : items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
        var goToPage = function (next) {
            return setPages(function (current) {
                var _a;
                return (__assign(__assign({}, current), (_a = {}, _a[sectionKey] = next, _a)));
            });
        };
        return (React.createElement("section", { className: css.section, "data-testid": "report-section-".concat(sectionKey) },
            React.createElement("h2", { className: css.sectionTitle },
                React.createElement(FormattedMessage, { id: titleId }),
                ' ',
                React.createElement("span", { className: css.sectionCount, "data-testid": "report-count-".concat(sectionKey) },
                    "(",
                    total,
                    ")")),
            items.length === 0 ? (
            /*
             * Two different reasons for a section to have no rows, and they must not share a
             * sentence. `total > 0` means the cap cut this section away entirely — the 1000
             * oldest rows all landed in the other section — so "there are no items here" under a
             * heading reading "(300)" would be flatly false on the paper. Found by blind review.
             */
            React.createElement("p", { className: css.sectionEmpty, "data-testid": "report-section-empty-".concat(sectionKey) },
                React.createElement(FormattedMessage, { id: total > 0
                        ? 'ui-czna-authorities.proposals.report.section.cutByCap'
                        : 'ui-czna-authorities.proposals.report.section.empty', values: { total: total } }))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: css.cards }, displayedItems.map(function (item) { return (React.createElement(ProposalReportCard, { key: item.id, item: item, getOrganizationName: getOrganizationName, getUserName: getUserName })); })),
                !renderAllForPrint && pageCount > 1 && (React.createElement("div", { className: css.pagination, "data-testid": "report-pagination-".concat(sectionKey) },
                    React.createElement(Button, { "data-testid": "report-pagination-".concat(sectionKey, "-prev"), disabled: page === 0, onClick: function () { return goToPage(page - 1); } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.pagination.previous" })),
                    React.createElement("span", { className: css.paginationLabel, "data-testid": "report-pagination-".concat(sectionKey, "-label") },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.pagination.pageOf", values: { page: page + 1, pageCount: pageCount } })),
                    React.createElement(Button, { "data-testid": "report-pagination-".concat(sectionKey, "-next"), disabled: page >= pageCount - 1, onClick: function () { return goToPage(page + 1); } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.pagination.next" }))))))));
    };
    return (React.createElement("div", { className: css.sheet, "data-testid": "proposal-report-sheet" },
        React.createElement("h1", { className: css.title },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.sheetTitle" })),
        React.createElement("p", { className: css.meta },
            React.createElement("span", { "data-testid": "report-period" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.period", values: { from: formatDate(report.from), to: formatDate(report.to) } })),
            ' · ',
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.generatedAt", values: { at: formatDateTime(report.generatedAt) } })),
        React.createElement("p", { className: css.meta, "data-testid": "report-total" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.total", values: {
                    total: report.totalCount,
                    createNew: report.createNewCount,
                    updateExisting: report.updateExistingCount,
                } })),
        report.truncated && (React.createElement("p", { className: css.truncated, "data-testid": "report-truncated" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.truncated", values: { shown: report.items.length, total: report.totalCount } }))),
        report.totalCount === 0 ? (React.createElement("p", { className: css.empty, "data-testid": "report-empty" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.empty" }))) : (React.createElement(React.Fragment, null,
            renderSection('ui-czna-authorities.proposals.report.section.new', newAuthorities, report.createNewCount, false),
            renderSection('ui-czna-authorities.proposals.report.section.updates', amendments, report.updateExistingCount, true)))));
};
export default ProposalReportSheet;
